import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { isTermlyReady, openTermlyPreferences } from '../../consent/cmp.ts';
import '../../components/legal/LegalShell.css';

/**
 * Dedicated cookie-preferences control. `TermlyLoader` injects the Termly CMP
 * for this route (for any auth state), which is how signed-in users — who never
 * see the home-page banner — can review or withdraw their tracking consent.
 *
 * Termly's preference handler only exists once its script has loaded, so we poll
 * briefly for readiness and then auto-open the modal. The visible link below is
 * the manual fallback if Termly is slow or blocked by an extension. This is the
 * TRACKING-consent control; account-level agreement consent is managed on the
 * Account page (see `docs/privacy-consent.md`).
 */
export default function CookiePreferences() {
  const openedRef = useRef(false);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 40; // ~4s at a 100ms cadence
    const timer = window.setInterval(() => {
      attempts += 1;
      if (openedRef.current) {
        window.clearInterval(timer);
        return;
      }
      if (isTermlyReady() && openTermlyPreferences()) {
        openedRef.current = true;
        window.clearInterval(timer);
        return;
      }
      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <article className="om-legal">
      <Link to="/" className="om-legal__home">
        ← OculusMind.AI
      </Link>
      <header className="om-legal__header">
        <h1>Cookie Preferences</h1>
      </header>
      <div className="om-legal__body">
        <p>
          Manage the cookies and similar tracking technologies OculusMind.AI
          uses. The preferences panel should open automatically. If it does not,
          use the link below.
        </p>
        <p>
          {/* Termly wires any `.termly-display-preferences` element to its modal
              via a delegated click listener; preventDefault keeps the `#` href
              from scroll-jumping or pushing a hash onto the router URL. */}
          <a
            href="#"
            className="termly-display-preferences"
            onClick={(e) => e.preventDefault()}
          >
            Open consent preferences
          </a>
        </p>
        <p>
          For details on what each category of cookie does, see our{' '}
          <Link to="/legal/cookie-policy">Cookie Policy</Link>. For how we handle
          personal data more broadly, see our{' '}
          <Link to="/legal/privacy">Privacy Policy</Link>.
        </p>
      </div>
    </article>
  );
}
